import { Component, ElementRef, ViewChild } from '@angular/core';
import { VOCCourseModel } from 'src/app/core/VOCCourseModel';
import { AdminService } from 'src/app/services/admin/admin.service';
import { FileUploadService } from 'src/app/services/file-upload/file-upload.service';

type CorporateSection = 'BOOKING' | 'VOC';

@Component({
  selector: 'app-corporate',
  templateUrl: './corporate.component.html',
  styleUrl: './corporate.component.scss'
})
export class CorporateComponent {
  @ViewChild('vocFormFileInput')
  vocFormFileInput?: ElementRef<HTMLInputElement>;

  selectedSection: CorporateSection = 'BOOKING';

  isVocFileUploading = false;
  vocFileErrorMessage = '';
  selectedVocFile: File | null = null;
  selectedVocFileName = '';
  vocEmailAddress = '';
  formUrl = '';
  successMessage = '';
  errorMessage = '';
  isUploading = false;

  VOCCourseModel: VOCCourseModel = this.createEmptyVocModel();

  constructor(
    private adminService: AdminService,
    private uploadService: FileUploadService
  ) {
  }

  selectSection(section: CorporateSection): void {
    this.selectedSection = section;
  }

  onVocFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.vocFileErrorMessage = '';

    if (!file) {
      this.resetVocFile();
      return;
    }

    if (
      file.type !== 'application/pdf' &&
      !file.name.toLowerCase().endsWith('.pdf')
    ) {
      this.vocFileErrorMessage = 'Please select a valid PDF file.';
      input.value = '';
      this.selectedVocFile = null;
      this.selectedVocFileName = '';
      return;
    }

    const maximumFileSize = 10 * 1024 * 1024;

    if (file.size > maximumFileSize) {
      this.vocFileErrorMessage = 'The PDF file must be smaller than 10 MB.';
      input.value = '';
      this.selectedVocFile = null;
      this.selectedVocFileName = '';
      return;
    }

    this.selectedVocFile = file;
    this.selectedVocFileName = file.name;
    this.isVocFileUploading = true;
    this.formUrl = '';

    this.uploadService
      .uploadSingleFile(file, 'VOC-form')
      .subscribe({
        next: response => {
          this.isVocFileUploading = false;

          const uploadedUrl = response?.publicUrl || '';

          if (!uploadedUrl) {
            this.vocFileErrorMessage =
              'The PDF was uploaded, but the file URL was not returned.';
            return;
          }

          this.formUrl = uploadedUrl;
        },
        error: error => {
          console.error('VOC form upload failed:', error);
          this.isVocFileUploading = false;
          this.vocFileErrorMessage = 'PDF upload failed. Please try again.';
          this.formUrl = '';
          this.selectedVocFile = null;
          this.selectedVocFileName = '';
          input.value = '';
        }
      });
  }

  removeVocFile(): void {
    this.resetVocFile();
  }

  uploadVocForm(): void {
    this.successMessage = '';
    this.errorMessage = '';

    if (!this.formUrl || !this.vocEmailAddress.trim()) {
      this.errorMessage =
        'Please enter your email address and upload the form.';
      return;
    }

    this.isUploading = true;

    this.VOCCourseModel.clientEmail = this.vocEmailAddress.trim();
    this.VOCCourseModel.requestFormUrl = this.formUrl;
    this.VOCCourseModel.createdDate = null;
    this.VOCCourseModel.reminderSendOn = null;
    this.VOCCourseModel.modifedDate = null;

    this.adminService
      .saveVocForm(this.VOCCourseModel, 'INSERT')
      .subscribe({
        next: response => {
          this.isUploading = false;

          if (response?.isSuccess) {
            this.successMessage =
              response.message || 'VOC form uploaded successfully.';
            this.vocEmailAddress = '';
            this.resetVocFile();
            this.VOCCourseModel = this.createEmptyVocModel();
          } else {
            this.errorMessage =
              response?.message || 'Unable to upload the VOC form.';
          }
        },
        error: error => {
          this.isUploading = false;
          this.errorMessage =
            error?.error?.message ||
            'An error occurred while uploading the VOC form.';
        }
      });
  }

  private resetVocFile(): void {
    this.selectedVocFile = null;
    this.selectedVocFileName = '';
    this.formUrl = '';
    this.vocFileErrorMessage = '';
    this.isVocFileUploading = false;

    if (this.vocFormFileInput) {
      this.vocFormFileInput.nativeElement.value = '';
    }
  }

  private createEmptyVocModel(): VOCCourseModel {
    return {
      id: 0,
      jobNumber: '',
      requestFormUrl: '',
      clientEmail: '',
      paymentStatus: '',
      createdDate: null,
      isReminderSend: false,
      reminderSendOn: null,
      StripePaymentIntentId: '',
      modifedDate: null,
      courseFee: 0
    };
  }
}
